import { IonCol, IonGrid, IonRow, IonSpinner, IonText } from '@ionic/react';
import React, { useState } from 'react';

import { TrashSimpleIcon, XIcon } from '@phosphor-icons/react';

import { useImageLoad } from '../../../hooks/Utils/useImageLoad';
import { formatMiles } from '../../../utils/planes';
import { isComprobanteItem, isPdfFile } from '../../../utils/gallery';
import Modal from '../Modal';
import PdfThumbnail from '../PdfThumbnail';
import PdfViewer from '../PdfViewer';
import './Gallery.scss';
import { GalleryProps } from './types';

type GalleryImageProps = {
  src: string;
  alt: string;
  className: string;
};

const GalleryImage: React.FC<GalleryImageProps> = React.memo(({ src, alt, className }) => {
  const { imageLoaded, handleImageLoad } = useImageLoad(src);

  return (
    <div className="gallery-image-wrapper">
      {!imageLoaded && (
        <div className="gallery-image-spinner">
          <IonSpinner color="medium" />
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={className}
        onLoad={handleImageLoad}
        style={{ opacity: imageLoaded ? 1 : 0 }}
      />
    </div>
  );
});

const Gallery: React.FC<GalleryProps> = ({
  isOpen,
  onClose,
  fileUrl,
  mimeType,
  files,
  isValidating,
  allowDelete,
  onDelete,
  title = 'Comprobantes',
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  const hasVisibleMonto = (monto?: string | number): boolean => {
    if (monto === undefined || monto === null || monto === '') return false;

    return Number(monto) > 0;
  };

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImageIndex(null);
  };

  const selectedItem = selectedImageIndex !== null && files ? files[selectedImageIndex] : null;
  const selectedFile = selectedItem
    ? isComprobanteItem(selectedItem)
      ? selectedItem.file
      : selectedItem
    : null;
  const isSelectedPdf = selectedFile ? isPdfFile(selectedFile) : false;

  return (
    <Modal isOpen={isOpen} onClose={onClose} navTitle={title}>
      <div className="gallery-content ion-padding">
        {isValidating ? (
          <div className="spinner-container">
            <IonSpinner color="dark" />
          </div>
        ) : files ? (
          <>
            <IonGrid>
              <IonRow>
                {files.map((item, index) => {
                  const file = isComprobanteItem(item) ? item.file : item;
                  const concepto = isComprobanteItem(item) ? item.concepto : undefined;
                  const monto = isComprobanteItem(item) ? item.monto : undefined;
                  const showMonto = hasVisibleMonto(monto);

                  const isPdf = isPdfFile(file);
                  const hasMeta = Boolean(concepto) || showMonto;

                  return (
                    <IonCol key={index} size="6">
                      <div
                        className={`gallery-item ${hasMeta ? 'gallery-item--with-meta' : ''} gallery-item--clickable`}
                        onClick={() => handleImageClick(index)}
                      >
                        {allowDelete && onDelete && (
                          <button
                            className="gallery-item__delete-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(index);
                            }}
                          >
                            <TrashSimpleIcon size={16} weight="bold" />
                          </button>
                        )}

                        {isPdf ? (
                          <PdfThumbnail
                            url={file}
                            variant="thumbnail"
                            onClick={() => {
                              // No hacer nada - el click lo maneja el gallery-item
                            }}
                          />
                        ) : (
                          <GalleryImage
                            src={file}
                            className="image-in-files"
                            alt={`Comprobante ${index}`}
                          />
                        )}

                        {hasMeta && (
                          <div className="gallery-item__meta">
                            {concepto && <IonText className="concepto">{concepto}</IonText>}
                            {showMonto && (
                              <IonText className="monto">${formatMiles(monto)}</IonText>
                            )}
                          </div>
                        )}
                      </div>
                    </IonCol>
                  );
                })}
              </IonRow>
            </IonGrid>

            {selectedFile && (
              <div className="gallery-lightbox" onClick={closeLightbox}>
                <div className="gallery-lightbox__backdrop" />
                <button className="gallery-lightbox__close" onClick={closeLightbox}>
                  <XIcon size={24} weight="bold" />
                </button>
                <div className="gallery-lightbox__content" onClick={(e) => e.stopPropagation()}>
                  {isSelectedPdf ? (
                    <PdfViewer url={selectedFile} />
                  ) : (
                    <img src={selectedFile} alt="Vista ampliada" />
                  )}
                </div>
              </div>
            )}
          </>
        ) : fileUrl ? (
          isPdfFile(fileUrl, mimeType) ? (
            <PdfViewer url={fileUrl} />
          ) : (
            <GalleryImage src={fileUrl} className="gallery-image" alt="Imagen desde S3" />
          )
        ) : null}
      </div>
    </Modal>
  );
};

export default React.memo(Gallery);
